import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	MaterialReactTable,
	MRT_ToggleFiltersButton,
	useMaterialReactTable,
} from "material-react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faUser,
	faChartBar,
	faShip,
	faAnchor,
	faBuilding,
} from "@fortawesome/free-solid-svg-icons";

import "./eventLogs.scss";

import { Button } from "abs-ui-components";
import { Icon, ICONS } from "abs-ui-components";

import axiosHttp from "../../../services/interceptor";
import { getEventLogsData } from "../../../services/AdminService";
import { convertLocaltoUTCFormat } from "../../../services/DateFormatUtility";
import BreadCrumb from "../../../common/breadCrumb/BreadCrumb";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const EventLogsData = () => {
	const [eventLogsData, setEventLogsData] = useState([]);
	const [selectAll, setSelectAll] = useState(false);
	const [loader, setLoader] = useState(false);
	const [columnFilters, setColumnFilters] = useState([]);
	const [pendingColumnFilters, setPendingColumnFilters] = useState(columnFilters);

	useEffect(() => {
		fetchEventLogsData();
	}, []);


	const fetchEventLogsData = async () => {
		setLoader(true);
		const eventLogsResponse = await getEventLogsData();
		setEventLogsData(eventLogsResponse);
		setLoader(false);
	};

	const eventTypes = {
		REPORT: faChartBar,
		USER: faUser,
		VESSEL: faShip,
		PORTCODE: faAnchor,
		WCN: faBuilding,
	};

	const resetFilterForColumn = useCallback((columnId) => {

		setPendingColumnFilters((prev) =>
			prev.filter((f) => f.id !== columnId)
		);

	}, []);

	const handleColumnFiltersChange = useCallback(
		(updater) => {

			setColumnFilters((prev) => {

				const updated =
					typeof updater === "function"
						? updater(prev)
						: updater;

				// detect removed filters
				prev.forEach((oldFilter) => {

					const stillExists = updated.some(
						(newFilter) =>
							newFilter.id === oldFilter.id
					);

					if (!stillExists) {

						// ONLY if previously applied
						const wasApplied =
							pendingColumnFilters.some(
								(f) => f.id === oldFilter.id
							);

						if (wasApplied) {
							resetFilterForColumn(oldFilter.id);
						}
					}
				});

				return updated;
			});
		},
		[pendingColumnFilters, resetFilterForColumn]
	);

	const applyFilter = useCallback(() => {

		setPendingColumnFilters(
			columnFilters
		);

	}, [columnFilters]);

	const updateFilterValue = useCallback(
		(columnId, value) => {

			setColumnFilters((prev) => {

				const normalizedValue =
					value?.trim?.() ?? "";

				const others = prev.filter(
					(f) => f.id !== columnId
				);

				const updatedPending = normalizedValue
					? [
						...others,
						{
							id: columnId,
							value: normalizedValue,
						},
					]
					: others;

				// ONLY auto reset if filter was already applied
				const wasApplied =
					pendingColumnFilters.some(
						(f) => f.id === columnId
					);

				if (!normalizedValue && wasApplied) {

					setPendingColumnFilters((prevApplied) =>
						prevApplied.filter(
							(f) => f.id !== columnId
						)
					);
				}

				return updatedPending;
			});
		},
		[pendingColumnFilters]
	);

	const tableHeader = [
		{
			header: "Event Type",
			accessorKey: "EventType",
			enableColumnFilter: false,
			enableColumnActions: false,
			accessorFn: (row) => {
				if (row.EventType) {
					return (
						<div className="text-left">
							<FontAwesomeIcon
								icon={eventTypes[row.EventType]}
							></FontAwesomeIcon>{" "}
							<span>{row.EventType}</span>
						</div>
					);
				}
			},
		},

		{
			header: "Subject",
			accessorKey: "Subject",
		},
		{
			header: "Event Status",
			accessorKey: "EventStatus",
			enableColumnFilter: false,
			enableColumnActions: false,
		},

		{
			header: "Event Time",
			accessorKey: "TimeStamp",
			enableColumnFilter: false,
			enableColumnActions: false,
			accessorFn: (row) => {
				if (row.TimeStamp) {
					return convertLocaltoUTCFormat(row.TimeStamp);
				}
			},
		},
		{
			header: "Processing Time",
			accessorKey: "ProcessingTime",
			enableColumnFilter: false,
			enableColumnActions: false,
		},
		{
			header: "Event Data",
			accessorKey: "EventData",
			enableColumnFilter: false,
			enableColumnActions: false,
		},
		{
			header: "Action",
			accessorKey: "actions",
			enableEditing: true,
			enableColumnFilter: false,
			enableColumnActions: false,
			Cell: ({ row }) => {
				const type = row.original.Subject;
				const eventType = row.original.EventType;
				const payload = JSON.parse(row.original.EventData);

				return (
					<div className="button-status d-flex">
						{eventType === "REPORT" && (
							<Button
								className="p-1"
								radius={"2px"}
								onClick={() => rerunEvent(type, payload)}
							>
								Re-Run Report Event
							</Button>
						)}
					</div>
				);
			},
		},
	];

	const breadcrumbs = useMemo(() => [
		{
			label: "Admin",
			url: "/admin",
			navId: "admin_navId",
		},
		{
			label: "Event Logs",
			url: "",
			navId: "event_logs",
		},
	]);

	const rerunEvent = (type, payload) => {
		setLoader(true);
		axiosHttp
			.post(
				`${BASE_URL}api/Events/ProcessReportEvent?eventType=${type}`,
				payload
			)
			.then((response) => {
				setLoader(false);
			});
	};

	const handleRefreshEventLogs = () => {
		fetchEventLogsData();
	};

	const handleSelectAll = (event) => {
		setSelectAll(event.target.checked);
	};

	const filteredData = useMemo(() => {
		if (!pendingColumnFilters?.length) return eventLogsData || [];
		return (eventLogsData || []).filter((row) =>
			pendingColumnFilters.every(({ id, value }) => {
				if (value == null || value === "") return true;
				const cell = row?.[id];
				if (cell == null) return false;
				return String(cell).toLowerCase().includes(String(value).toLowerCase());
			})
		);
	}, [eventLogsData, pendingColumnFilters]);

	const custTable = useMaterialReactTable({
		columns: tableHeader,
		data: filteredData,
		enableStickyHeader: true,
		enablePagination: false,
		enableFilters: true,
		enableColumnFilters: true,
		enableColumnPinning: false,
		enableSorting: true,
		enableColumnVirtualization: true,
		enableEditing: false,
		enableRowSelection: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		columnResizeMode: true,
		manualFiltering: true,
		state: {
			isLoading: loader,
			columnFilters: columnFilters,
		},
		onColumnFiltersChange: handleColumnFiltersChange,
		//for future scope
		renderToolbarInternalActions: ({ table }) => (
			<>
				<MRT_ToggleFiltersButton
					id="vessel_view_global_filter"
					table={table}
				/>
			</>
		),
		muiTableHeadCellProps: ({ column }) => ({
			id: `${column.columnDef.accessorKey}`,
			sx: {
				color: "rgb(255 255 255)",

				justifyContent: "center",
				"& .MuiButtonBase-root": {
					color: "white !important",
					"& .MuiTableSortLabel-icon": {
						color: "white !important",
					},
				},
			},
		}),
		muiFilterTextFieldProps: ({ column }) => ({
			sx: {
				"& .MuiInput-root": {
					color: "white !important",
				},
				"& .MuiButtonBase-root": {
					color: "white !important",
				},
				"& .MuiInputBase-root::after": {
					transform: "none",
					borderBottom: "2px solid white",
				},
			},
			onChange: (e) => {

				const value = e.target.value;

				updateFilterValue(column.id, value);
			},
			onKeyDown: (e) => {

				if (e.key === "Enter") {
					applyFilter();
					}
				},
		}),
		muiColumnActionsButtonProps: {
			sx: {
				color: "white",
			},
		},
		muiSelectAllCheckboxProps: {
			title: selectAll ? "Toggle deselect all" : "Toggle select all",
			onClick: (e) => handleSelectAll(e),
			sx: {
				color: "white",
			},
		},
		muiTableHeadRowProps: {
			sx: {
				backgroundColor: "#042a4c !important",

				"& .MuiTableCell-head::before": {
					backgroundColor: "#042a4c !important",
				},

				"& .MuiDivider-root": {
					backgroundColor: "white",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
	});
	return (
		<>
			<div className="actions-top mt-2 ml-4">
				<BreadCrumb items={breadcrumbs} />
			</div>
			<div className="bold mt-2 ml-4 mr-4">
				<h4>
					Event Logs{" "}
					<span
						className="refresh-icon"
						onClick={handleRefreshEventLogs}
					>
						<Icon icon={ICONS.REFRESH} />
					</span>
				</h4>
			</div>
			<div className="table-container mt-2 ml-4 mr-4">
				<MaterialReactTable table={custTable} />
			</div>
		</>
	);
};
export default EventLogsData;
